/* eslint-disable functional/prefer-readonly-type */
import { ApolloError } from "@apollo/client";
import { ReadonlyArray, pipe } from "@morphism/fp";
import { Progress } from "antd";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { Button } from "~/ButtonV2";
import * as Color from "~/Color";
import { DVIR, DVIRs } from "~/DVIR";
import { Graph } from "~/Graph";
import { ID } from "~/ID";
import { Mutable } from "~/Mutable";
import { Table as GenericTable } from "~/TableV2";
import { Time } from "~/Time";
import { Toast } from "~/Toast";

import { Assets } from "./Assets";
import { Date } from "./Date";
import { Defects } from "./Defects";
import { Driver } from "./Driver";
import { DateCreated } from "./DateCreated";

import { Status as Status_ } from "./Status";
import { Type } from "./Type";

export function Table({
  dvirs,
  connection,
  settings,
  setSettings,
  isLoading,
  isFetchingMore,
  errors,
}: {
  readonly dvirs: DVIRs;
  readonly connection: DVIRs.Query.Connection;
  readonly settings: ReturnType<typeof Table.Settings.use>[0];
  readonly setSettings: ReturnType<typeof Table.Settings.use>[1];
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly errors?: ApolloError;
}) {
  const [selectedDVIR, setSelectedDVIR] = useState<ID | undefined>(undefined);
  const { totalCount, pageInfo, edges } = connection;

  const dvir =
    selectedDVIR && dvirs.find((dvir) => dvir && dvir.id === selectedDVIR.id);

  return (
    <Table.Container>
      {dvir && <DVIR onClose={() => setSelectedDVIR(undefined)} dvir={dvir} />}
      <GenericTable
        columns={Table.columns(dvirs as Mutable<typeof dvirs>, [
          settings,
          setSettings,
        ])}
        dataSource={dvirs as Mutable<typeof dvirs>}
        loading={isLoading}
        pagination={{
          showSizeChanger: true,
          position: ["bottomCenter"],
          disabled: isLoading,
          showTotal: () =>
            (isLoading || isFetchingMore) && pageInfo.hasPreviousPage ? (
              <Progress
                percent={Math.round(
                  (Math.max(edges.length, 0) / Math.max(totalCount, 1)) * 100
                )}
                type="line"
                strokeLinecap="square"
                strokeColor={Color.azure()}
                style={{
                  alignSelf: "center",
                  paddingRight: "8px",
                  width: "112px",
                }}
              />
            ) : null,
        }}
        onRow={(dvir) => ({ onClick: () => setSelectedDVIR(dvir) })}
      />
      {errors
        ? Graph.Errors.renderApolloError("Error retrieving DVIRs", errors)
        : null}
    </Table.Container>
  );
}

export namespace Table {
  // TODO: Move to `DVIR.Status`
  export import Status = Status_;

  export const columns = (
    // eslint-disable-next-line functional/prefer-readonly-type
    dvirs: DVIR[],
    [settings, setSettings]: ReturnType<typeof Settings.use>
  ) => [
    Date.column({
      ...settings,
      onChange: (interval) => setSettings({ interval: interval?.interval }),
    }),

    DateCreated.column(),

    Type.column({
      ...settings,
      onChange: (types) => setSettings({ types }),
    }),

    Status.column({
      ...settings,
      onChange: (status) => setSettings({ status: status as Status.StatusEnum[] }),
    }),

    Driver.column({
      dvirs,
      ...settings,
      onChange: (drivers) => setSettings({ drivers }),
    }),

    Assets.column("vehicle")({
      dvirs,
      assets: settings.vehicles,
      onChange: (vehicles) => setSettings({ vehicles }),
    }),

    Defects.column("vehicle")({
      dvirs,
      types: settings?.defectTypes?.vehicles,
      onChange: (types) =>
        setSettings({
          defectTypes: { ...settings.defectTypes, vehicles: types },
        }),
    }),

    Assets.column("trailers")({
      dvirs,
      assets: settings.trailers,
      onChange: (trailers) => setSettings({ trailers }),
    }),

    Defects.column("trailers")({
      dvirs,
      types: settings?.defectTypes?.trailers,
      onChange: (types) =>
        setSettings({
          defectTypes: { ...settings.defectTypes, trailers: types },
        }),
    }),
  ];

  export const Container = styled.div`
    position: relative;

    td {
      vertical-align: top;
    }
  `;

  export const PaginationContainer = styled.span`
    & .ant-progress-text {
      color: ${Color.azure()} !important;
    }
  `;

  export namespace Error {
    export const renderErrors = (action: string, error: ApolloError) => {
      const message = `Error ${action}`;
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        return (
          <ErrorContainer>
            {
              void error.graphQLErrors.forEach((error) => (
                <Toast message={message} description={error.message} />
              ))
            }
          </ErrorContainer>
        );
      }
      if (error.clientErrors && error.clientErrors.length > 0) {
        return (
          <ErrorContainer>
            {
              void error.clientErrors.forEach((error) => (
                <Toast message={message} description={error.message} />
              ))
            }
          </ErrorContainer>
        );
      }
      if (error.networkError) {
        return (
          <Toast message={message} description={error.networkError.message} />
        );
      }
      return <Toast message={message} description={error.message} />;
    };

    export const ErrorContainer = styled.div`
      display: flex;
    `;
  }

  export type Settings = Partial<Time.Interval.Stopped> & {
    readonly drivers?: readonly ID[];
    readonly types?: readonly DVIR.Type[];
    readonly status?: Status.StatusEnum[];
    readonly vehicles?: readonly ID[];
    readonly trailers?: readonly ID[];
    readonly defectTypes?: {
      readonly vehicles?: readonly ID[];
      readonly trailers?: readonly ID[];
    };
  };

  export namespace Settings {
    export const decode = (query: string): Settings =>
      pipe(new URLSearchParams(query), (params) => {
        params.get("trailers");

        return {};
      });

    export const encode = (settings: Partial<Settings>): string => {
      const interval =
        settings.interval &&
        pipe(
          settings.interval,
          ({ start, stop }) => [start, stop],
          ReadonlyArray.map(Time.DateTime.toISOLocalWithoutTime),
          ([start, stop]) => [`start=${start}`, `stop=${stop}`]
        );

      const types =
        settings.types &&
        pipe(
          settings.types,
          ReadonlyArray.map(
            ({ type }) => `type=${type.toLocaleLowerCase().replace("_", "-")}`
          )
        );

      const isResolved =
        settings.status && `resolved=${settings.status.join(',')}`;

      const vehicles =
        settings.vehicles &&
        pipe(
          settings.vehicles,
          ReadonlyArray.map(({ id }) => `vehicles=${id}`)
        );

      const trailers =
        settings.trailers &&
        pipe(
          settings.trailers,
          ReadonlyArray.map(({ id }) => `trailer=${id}`)
        );

      return pipe(
        [
          ...(interval ?? []),
          ...(types ?? []),
          isResolved,
          ...(vehicles ?? []),
          ...(trailers ?? []),
        ],
        (parts) => `?${parts.join("&")}`
      );
    };

    export const use = (): readonly [
      settings: Settings,
      setSettings: (updated?: Partial<Settings>) => void
    ] =>
      pipe(
        React.useState(decode(useLocation().search)),
        ([settings, setSettings]) => [
          settings,
          (updated) => setSettings({ ...settings, ...updated }),
        ]
      );

    export const useURLUpdate = () => {
      const history = useHistory();
      return (updated: Partial<Settings> = {}) =>
        history.push(encode(updated ?? decode(window.location.search)));
    };
  }

  export function Filter({ children, ...props }: Filter.Props) {
    return (
      <>
        <Filter.Controls {...props} />
        <Filter.Container>{children}</Filter.Container>
      </>
    );
  }

  export namespace Filter {
    export type Props = {
      readonly loading?: boolean;
    } & React.PropsWithChildren<
      Controls.Props & { readonly confirm: () => void }
    >;

    export const Container = styled.div`
      padding: 0 4px 4px 4px;
    `;

    export function Controls({ onReset, confirm }: Controls.Props) {
      return (
        <Controls.Container>
          <Button
            type="link"
            size="small"
            disabled={!onReset}
            onClick={() => {
              onReset?.();
              confirm();
            }}
          >
            Reset
          </Button>
          <Button type="primary" size="small" onClick={confirm}>
            OK
          </Button>
        </Controls.Container>
      );
    }

    export namespace Controls {
      export type Props = {
        readonly confirm: () => void;
        readonly onReset?: () => void;
      };

      export const Container = styled.div`
        display: flex;
        justify-content: space-between;
        padding: 8px;
        margin-bottom: 4px;
        border-bottom: 1px solid #f0f0f0;
      `;
    }
  }
}
